import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Clock3, FolderOpen, Home, LogOut, TimerReset } from 'lucide-react';
import type { Project } from '../types/project';
import type { TimeEntry } from '../types/timeTracking';

interface EmployeeTimeTrackingScreenProps {
  employeeName: string;
  employeeEmail: string;
  projects: Project[];
  activeProjectId: string | null;
  currentEntry: TimeEntry | null;
  onSelectProject: (projectId: string) => void;
  onClockIn: () => void;
  onClockOut: () => void;
  onGoHome?: () => void;
  onLogout: () => void;
}

const formatDuration = (milliseconds: number) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map(value => String(value).padStart(2, '0')).join(':');
};

export function EmployeeTimeTrackingScreen({
  employeeName,
  employeeEmail,
  projects,
  activeProjectId,
  currentEntry,
  onSelectProject,
  onClockIn,
  onClockOut,
  onGoHome,
  onLogout,
}: EmployeeTimeTrackingScreenProps) {
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [tick, setTick] = useState(Date.now());

  useEffect(() => {
    if (!currentEntry) return;
    const interval = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [currentEntry]);

  const activeProject = useMemo(
    () => projects.find(project => project.id === activeProjectId) || null,
    [projects, activeProjectId]
  );

  const runningTimer = currentEntry
    ? formatDuration(tick - new Date(currentEntry.clockInAt).getTime())
    : '00:00:00';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            {onGoHome && (
              <button
                onClick={onGoHome}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-white rounded-lg transition shadow-sm hover:shadow-md bg-white/80"
              >
                <Home className="w-5 h-5" />
                <span>Home</span>
              </button>
            )}
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm">
              <span className="text-sm text-gray-600">Logged in as</span>
              <span className="text-sm font-semibold text-emerald-700">{employeeName}</span>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-white rounded-lg transition shadow-sm hover:shadow-md bg-white/80"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Clock In / Clock Out</h1>
            <p className="text-gray-600">Track time against your assigned project.</p>
            <p className="text-sm text-gray-500 mt-2">{employeeEmail}</p>
          </div>

          {activeProject && (
            <div className="relative">
              <button
                onClick={() => projects.length > 1 && setShowProjectPicker(prev => !prev)}
                className={`w-full flex items-center gap-3 px-5 py-4 bg-white rounded-xl shadow-sm border border-emerald-100 text-left transition ${projects.length > 1 ? 'hover:border-emerald-300 hover:shadow-md cursor-pointer' : 'cursor-default'}`}
              >
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <FolderOpen className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-0.5">Working on</div>
                  <div className="font-semibold text-gray-900">{activeProject.name}</div>
                  <div className="text-xs text-gray-500 font-mono">{activeProject.number}</div>
                </div>
                {projects.length > 1 && (
                  <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                    Switch <ChevronDown className="w-4 h-4" />
                  </div>
                )}
              </button>

              {showProjectPicker && projects.length > 1 && (
                <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
                  {projects.map(project => (
                    <button
                      key={project.id}
                      onClick={() => {
                        onSelectProject(project.id);
                        setShowProjectPicker(false);
                      }}
                      className={`w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-emerald-50 transition ${project.id === activeProjectId ? 'bg-emerald-50' : ''}`}
                    >
                      <FolderOpen className="w-4 h-4 text-emerald-500" />
                      <div>
                        <div className="font-medium text-gray-900">{project.name}</div>
                        <div className="text-xs text-gray-500 font-mono">{project.number}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Clock3 className="w-5 h-5 text-emerald-600" />
                <h2 className="text-lg font-semibold text-gray-900">Current Status</h2>
              </div>
              <p className={`text-2xl font-bold ${currentEntry ? 'text-emerald-600' : 'text-gray-900'}`}>
                {currentEntry ? 'Clocked In' : 'Ready to Clock In'}
              </p>
              {currentEntry && (
                <p className="text-sm text-gray-600 mt-3">
                  Started at {new Date(currentEntry.clockInAt).toLocaleString()}
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
              <div className="flex items-center gap-3 mb-4">
                <TimerReset className="w-5 h-5 text-emerald-600" />
                <h2 className="text-lg font-semibold text-gray-900">Running Timer</h2>
              </div>
              <p className="text-4xl font-bold text-emerald-700 tracking-tight">{runningTimer}</p>
              <p className="text-sm text-gray-600 mt-3">
                {currentEntry ? 'Your time is being tracked now.' : 'Start the timer by clocking in.'}
              </p>
            </div>
          </div>

          {currentEntry ? (
            <button
              onClick={onClockOut}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-6 rounded-xl transition duration-200 shadow-lg hover:shadow-xl"
            >
              Clock Out
            </button>
          ) : (
            <button
              onClick={onClockIn}
              disabled={!activeProjectId}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-xl transition duration-200 shadow-lg hover:shadow-xl"
            >
              Clock In
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
